import { detectEcommercePlatform } from './connectionPlatform';
import {
  connectionHasAmazonInventoryCsv,
  connectionHasShopifyInventoryCsv,
  isCsvLikeConnection,
} from './inventoryCsvConnectionChannels';

/**
 * Derives the "Data Source" labels shown in the Current Stock tab's source picker.
 *
 * Extracted from OrganisationInventoryTab's `availableSources` memo so tests can assert on
 * the real derivation instead of a hand-copied replica. The replica had already drifted --
 * it omitted the CSV-import labels entirely, so it could not have caught a regression in
 * the CSV branches or in label ordering.
 *
 * Label order is meaningful: it is the order the options are rendered in, and
 * `getSourceDisplayText` compares selected-vs-available lengths to decide when to collapse
 * the picker to "All Sources".
 *
 * Veeqo intentionally has no CSV variant -- Veeqo inventory only arrives over the API, so a
 * connection that claims to be Veeqo-over-CSV is not a recognised source.
 */
export function getInventoryTabAvailableSources(
  connections: Record<string, unknown> | unknown[] | null | undefined,
): string[] {
  if (!connections) return [];

  const list = (Array.isArray(connections) ? connections : Object.values(connections)) as Record<
    string,
    unknown
  >[];

  const hasShopify = list.some((conn) => detectEcommercePlatform(conn) === 'shopify');

  const hasShopifyCsv = list.some((conn) => {
    if (connectionHasShopifyInventoryCsv(conn)) return true;
    if (!isCsvLikeConnection(conn)) return false;
    const rawSub = String(conn.ecommerce_subtype || '').toLowerCase();
    return rawSub === 'shopify' && !connectionHasAmazonInventoryCsv(conn);
  });

  const hasAmazon = list.some((conn) => detectEcommercePlatform(conn) === 'amazon');

  const hasAmazonCsv = list.some((conn) => {
    if (connectionHasAmazonInventoryCsv(conn)) return true;
    if (!isCsvLikeConnection(conn)) return false;
    const rawSub = String(conn.ecommerce_subtype || '').toLowerCase();
    return rawSub === 'amazon' && !connectionHasShopifyInventoryCsv(conn);
  });

  const hasVeeqo = list.some((conn) => detectEcommercePlatform(conn) === 'veeqo');

  const sources: string[] = [];
  if (hasShopify) sources.push('Shopify');
  if (hasShopifyCsv) sources.push('Shopify CSV Import');
  if (hasAmazon) sources.push('Amazon');
  if (hasAmazonCsv) sources.push('Amazon CSV Import');
  if (hasVeeqo) sources.push('Veeqo');

  return sources;
}
